import { createFileRoute, Link, useRouter } from '@tanstack/react-router';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { api } from '../lib/api';
import { requireAccess } from '../lib/guards';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import { ErrorBanner } from '../components/ui/ErrorBanner';
import { PageHeader } from '../components/ui/PageHeader';
import type { AnfrageTyp } from '../lib/api-enums';
import type { AnfrageQuelle as AnfrageQuelleAll } from '../lib/api-types';

// FE-Restriktion: Manuelle Erfassung deckt nur die 3 nicht-automatischen Quellen ab.
type AnfrageQuelle = Extract<AnfrageQuelleAll, 'telefon' | 'caretaker' | 'sonstige'>;

const QUELLE_OPTIONS: Array<[AnfrageQuelle, string]> = [
  ['telefon', 'Telefon'],
  ['caretaker', 'Caretaker / Hausmeister'],
  ['sonstige', 'Sonstige'],
];

const TYP_OPTIONS: Array<[AnfrageTyp, string]> = [
  ['reparatur', 'Reparatur'],
  ['wartung', 'Wartung'],
  ['notdienst', 'Notdienst'],
  ['reklamation', 'Reklamation'],
  ['beratung', 'Beratung'],
  ['aufmass', 'Aufmaß'],
  ['sonstige', 'Sonstige'],
];

interface Kunde {
  id: string;
  kundennummer: string;
  firmenname: string;
}

interface Objekt {
  id: string;
  bezeichnung: string;
  kundeId: string;
}

export const Route = createFileRoute('/inbox/neu')({
  beforeLoad: () => requireAccess('/api/v1/inbox'),
  component: InboxNeuPage,
});

function InboxNeuPage() {
  const router = useRouter();
  const kundenQ = useQuery({
    queryKey: ['kunden', 'lookup'],
    queryFn: () => api<{ items: Kunde[] }>('/api/v1/kunden'),
  });
  const objekteQ = useQuery({
    queryKey: ['objekte', 'lookup'],
    queryFn: () => api<{ items: Objekt[] }>('/api/v1/objekte'),
  });

  const [quelle, setQuelle] = useState<AnfrageQuelle>('telefon');
  const [absenderName, setAbsenderName] = useState('');
  const [absenderEmail, setAbsenderEmail] = useState('');
  const [betreff, setBetreff] = useState('');
  const [text, setText] = useState('');
  const [anfrageTyp, setAnfrageTyp] = useState<AnfrageTyp | ''>('');
  const [kundeId, setKundeId] = useState('');
  const [objektId, setObjektId] = useState('');
  const [error, setError] = useState<unknown>(null);

  const objekteDesKunden = (objekteQ.data?.items ?? []).filter((o) => o.kundeId === kundeId);

  const save = useMutation({
    mutationFn: () =>
      api<{ item: { id: string } }>('/api/v1/inbox', {
        method: 'POST',
        body: {
          quelle,
          absenderName: absenderName || null,
          absenderEmail: absenderEmail || null,
          betreff: betreff || null,
          text: text || null,
          anfrageTyp: anfrageTyp || null,
          kundeId: kundeId || null,
          objektId: objektId || null,
        },
      }),
    onSuccess: async (res) => {
      await router.navigate({ to: '/inbox/$id', params: { id: res.item.id } });
    },
    onError: (err) => setError(err),
  });

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    save.mutate();
  }

  return (
    <div className="space-y-4">
      <div>
        <Link to="/inbox" className="text-sm text-brand-700 hover:underline">
          ← zurück zur Inbox
        </Link>
      </div>
      <PageHeader
        title="Neue Anfrage"
        description="Manueller Eintrag aus Telefon oder Caretaker-Meldung."
      />
      {error ? <ErrorBanner error={error} /> : null}

      <form
        onSubmit={handleSubmit}
        className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm space-y-4"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
          <div>
            <label className="text-xs font-medium text-slate-600">Quelle *</label>
            <Select value={quelle} onChange={(e) => setQuelle(e.target.value as AnfrageQuelle)}>
              {QUELLE_OPTIONS.map(([v, l]) => (
                <option key={v} value={v}>
                  {l}
                </option>
              ))}
            </Select>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600">Anfrage-Typ</label>
            <Select
              value={anfrageTyp}
              onChange={(e) => setAnfrageTyp(e.target.value as AnfrageTyp | '')}
            >
              <option value="">— offen / spaeter triagieren —</option>
              {TYP_OPTIONS.map(([v, l]) => (
                <option key={v} value={v}>
                  {l}
                </option>
              ))}
            </Select>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600">Anrufer / Absender (Name)</label>
            <Input
              value={absenderName}
              onChange={(e) => setAbsenderName(e.target.value)}
              placeholder="z.B. Frau Schmidt"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600">E-Mail (optional)</label>
            <Input
              type="email"
              value={absenderEmail}
              onChange={(e) => setAbsenderEmail(e.target.value)}
              placeholder="schmidt@beispiel.de"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600">Kunde (falls bekannt)</label>
            <Select
              value={kundeId}
              onChange={(e) => {
                setKundeId(e.target.value);
                setObjektId('');
              }}
            >
              <option value="">— offen —</option>
              {(kundenQ.data?.items ?? []).map((k) => (
                <option key={k.id} value={k.id}>
                  {k.kundennummer} — {k.firmenname}
                </option>
              ))}
            </Select>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600">Objekt (optional)</label>
            <Select
              value={objektId}
              disabled={!kundeId}
              onChange={(e) => setObjektId(e.target.value)}
            >
              <option value="">— keines —</option>
              {objekteDesKunden.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.bezeichnung}
                </option>
              ))}
            </Select>
          </div>
        </div>
        <div>
          <label className="text-xs font-medium text-slate-600">Betreff *</label>
          <Input
            value={betreff}
            onChange={(e) => setBetreff(e.target.value)}
            placeholder="Kurzfassung"
            required
          />
        </div>
        <div>
          <label className="text-xs font-medium text-slate-600">Beschreibung</label>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={5}
            className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
            placeholder="Worum geht's? Adresse, Schaden, Dringlichkeit, ..."
          />
        </div>
        <div className="flex justify-end pt-2">
          <Button type="submit" disabled={save.isPending}>
            {save.isPending ? 'Speichere …' : 'Anfrage anlegen'}
          </Button>
        </div>
        <p className="text-xs text-slate-500">
          Tipp: wenn Kunde + Typ gesetzt sind, startet die SLA-Frist sofort. Andernfalls bleibt die
          Anfrage im Status "neu" bis zur Triage.
        </p>
      </form>
    </div>
  );
}
